import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, FileText, Trash2, Download, Eye, Edit, Copy, Send, Bot, User, Search, Sparkles, FileSpreadsheet, ArrowLeft, ArrowUp, ArrowDown, Layers, Repeat } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const SECTION_COLORS = [
  { bg: '#DCEEFB', text: '#1a4b8c' },
  { bg: '#FDE8D7', text: '#c2610c' },
  { bg: '#E3F5E1', text: '#2f7a2f' },
  { bg: '#FBE7F0', text: '#a13d72' },
  { bg: '#FFF6D9', text: '#8a6d00' },
  { bg: '#E7E3FB', text: '#5b4bb5' },
  { bg: '#E0F7F5', text: '#0f7d72' },
  { bg: '#FBE3E3', text: '#b23b3b' },
];

// Le vecchie voci "titolo sezione" (righe finte nell'array items) vengono convertite
// nel nuovo modello: registro sezioni a parte + section_id sulle voci reali.
function normalizeQuoteSections(rawItems, rawSections) {
  let sections = Array.isArray(rawSections) ? [...rawSections] : [];
  const items = [];
  let currentSectionId = null;
  let legacyCount = 0;
  (rawItems || []).forEach((it) => {
    if (it.is_header) {
      const id = it.id || `sec_legacy_${legacyCount}`;
      sections.push({ id, title: it.title || '', color: it.color || SECTION_COLORS[legacyCount % SECTION_COLORS.length].bg, text_color: it.text_color || SECTION_COLORS[legacyCount % SECTION_COLORS.length].text });
      currentSectionId = id;
      legacyCount++;
    } else {
      items.push(currentSectionId && !it.section_id ? { ...it, section_id: currentSectionId } : it);
    }
  });
  return { sections, items };
}

export default function QuotesPage() {
  const { isRole } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [editingQuote, setEditingQuote] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [activeView, setActiveView] = useState('list');
  
  const [quoteForm, setQuoteForm] = useState({
    quote_number: '', client_name: '', client_address: '', client_email: '', client_phone: '',
    project_description: '', location: '', date: new Date().toISOString().split('T')[0],
    validity_days: 30, items: [], sections: [], discount_percentage: 0, notes: '',
    terms: 'Pagamento: 50% alla conferma, 50% a fine lavori.\nI prezzi si intendono IVA esclusa.',
    status: 'draft',
    ns_rif: '', vs_rif: '', work_order: '',
    oneri_nostri: 'Come da contratto', oneri_vostri: 'Come da contratto',
    attrezzatura: 'Come da contratto', inizio_lavoro: 'Come da contratto', durata_lavoro: 'Come da contratto',
    extra_note: '', extra_service_description: '', extra_service_amount: 0
  });
  
  const [itemForm, setItemForm] = useState({ price_item_id: '', quantity: '', unit: '', section_id: '', accessory_items: [] });
  const [searchItemTerm, setSearchItemTerm] = useState('');
  const [showItemResults, setShowItemResults] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [editingItemIdx, setEditingItemIdx] = useState(null);
  const [showCreateSectionDialog, setShowCreateSectionDialog] = useState(false);
  const [sectionTitleInput, setSectionTitleInput] = useState('');
  const [showChangeService, setShowChangeService] = useState(false);
  const [changeServiceIdx, setChangeServiceIdx] = useState(null);
  const [changeServiceSearch, setChangeServiceSearch] = useState('');
  const [chatMessages, setChatMessages] = useState([{ role: 'assistant', content: 'Ciao! Sono Rocchino Coverino. Descrivi il lavoro e ti aiuterò a trovare le voci dal prezziario.' }]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);

  // Bozza automatica: quello che scrivi in un preventivo viene salvato nel browser e si puo' riprendere
  // anche dopo un logout, una chiusura della scheda o un errore.
  const DRAFT_KEY = 'quote_draft_v1';
  const baselineRef = useRef('');
  const [draft, setDraft] = useState(null);
  const readDraft = useCallback(() => {
    try { const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); return d && d.quoteForm ? d : null; } catch { return null; }
  }, []);
  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignora */ } setDraft(null); };
  useEffect(() => { setDraft(readDraft()); }, [readDraft, activeView]);
  useEffect(() => {
    if (activeView === 'edit') baselineRef.current = JSON.stringify(quoteForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);
  useEffect(() => {
    if (activeView !== 'edit' || JSON.stringify(quoteForm) === baselineRef.current) return undefined;
    const t = setTimeout(() => {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ quoteForm, editingQuoteId: editingQuote?.id || null, savedAt: Date.now() })); } catch { /* ignora */ }
    }, 800);
    return () => clearTimeout(t);
  }, [quoteForm, activeView, editingQuote]);
  useEffect(() => {
    if (activeView !== 'edit') return undefined;
    const warn = (e) => { if (JSON.stringify(quoteForm) !== baselineRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [quoteForm, activeView]);
  const restoreDraft = () => {
    const d = readDraft();
    if (!d) return;
    setEditingQuote(d.editingQuoteId ? (quotes.find(q => q.id === d.editingQuoteId) || null) : null);
    setQuoteForm(d.quoteForm);
    setThreadId(null);
    setActiveView('edit');
  };
  const chatEndRef = useRef(null);
  
  const load = async () => {
    try {
      const [quotesRes, pricesRes] = await Promise.all([api.get('/quotes'), api.get('/price-lists')]);
      setQuotes(quotesRes.data);
      setPriceItems(pricesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  
  useEffect(() => { load(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);
  
  const fetchNextNumber = async () => {
    try { const res = await api.get('/quotes/next-number'); return res.data.next_number; }
    catch { return 'PV 26.01'; }
  };
  
  const handleNewQuote = async () => {
    const nextNum = await fetchNextNumber();
    setEditingQuote(null);
    setQuoteForm({
      quote_number: nextNum, client_name: '', client_address: '', client_email: '', client_phone: '',
      project_description: '', location: '', date: new Date().toISOString().split('T')[0],
      validity_days: 30, items: [], sections: [], discount_percentage: 0, notes: '',
      terms: 'Pagamento: 50% alla conferma, 50% a fine lavori.\nI prezzi si intendono IVA esclusa.', status: 'draft',
      ns_rif: '', vs_rif: '', work_order: '',
      oneri_nostri: 'Come da contratto', oneri_vostri: 'Come da contratto',
      attrezzatura: 'Come da contratto', inizio_lavoro: 'Come da contratto', durata_lavoro: 'Come da contratto',
      extra_note: '', extra_service_description: '', extra_service_amount: 0
    });
    setChatMessages([{ role: 'assistant', content: 'Ciao! Sono Rocchino Coverino. Descrivi il lavoro da preventivare e ti aiuterò.' }]);
    setThreadId(null);
    setActiveView('edit');
  };
  
  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    const { sections, items } = normalizeQuoteSections(quote.items, quote.sections);
    setQuoteForm({
      ...quote,
      items, sections,
      terms: quote.terms || 'Pagamento: 50% alla conferma, 50% a fine lavori.\nI prezzi si intendono IVA esclusa.',
      notes: quote.notes || '',
      bank_details: quote.bank_details || '',
      ns_rif: quote.ns_rif || '',
      vs_rif: quote.vs_rif || '',
      work_order: quote.work_order || '',
      oneri_nostri: quote.oneri_nostri || 'Come da contratto',
      oneri_vostri: quote.oneri_vostri || 'Come da contratto',
      attrezzatura: quote.attrezzatura || 'Come da contratto',
      inizio_lavoro: quote.inizio_lavoro || 'Come da contratto',
      durata_lavoro: quote.durata_lavoro || 'Come da contratto',
      extra_note: quote.extra_note || '',
      extra_service_description: quote.extra_service_description || '',
      extra_service_amount: quote.extra_service_amount || 0,
    });
    setThreadId(null);
    setActiveView('edit');
  };
  const handleDuplicateQuote = async (quote) => {
    const nextNum = await fetchNextNumber();
    setEditingQuote(null);
    const { sections, items } = normalizeQuoteSections(quote.items, quote.sections);
    setQuoteForm({ ...quote, items, sections, id: undefined, quote_number: nextNum, date: new Date().toISOString().split('T')[0], status: 'draft' });
    setThreadId(null);
    setActiveView('edit');
  };
  
  const handleSaveQuote = async () => {
    if (!quoteForm.client_name || !quoteForm.quote_number) { alert('Compila nome cliente e numero'); return; }
    try {
      if (editingQuote) { await api.put('/quotes/' + editingQuote.id, quoteForm); }
      else { await api.post('/quotes', quoteForm); }
      clearDraft();
      setActiveView('list'); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };
  
  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Eliminare?')) return;
    try { await api.delete('/quotes/' + quoteId); load(); } catch { alert('Errore'); }
  };
  
  const handleDownload = async (quoteId, format) => {
    try {
      const response = await api.get('/quotes/' + quoteId + '/' + format, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'Preventivo_' + quoteId + '.' + (format === 'excel' ? 'xlsx' : 'pdf'));
      document.body.appendChild(link); link.click(); link.remove();
    } catch { alert('Errore download'); }
  };
  
  const selectedPriceItem = priceItems.find(p => p.id === itemForm.price_item_id);
  const baseTotal = selectedPriceItem && itemForm.quantity ? (parseFloat(itemForm.quantity) || 0) * (selectedPriceItem.price_total || 0) : 0;
  const accessoryAdjustment = itemForm.accessory_items.reduce((sum, acc) => {
    const accItem = priceItems.find(p => p.id === acc.price_item_id);
    return accItem?.percentage_value ? sum + (baseTotal * (accItem.percentage_value / 100)) : sum;
  }, 0);
  const previewTotal = baseTotal + accessoryAdjustment;
  
  const handleAddItemToQuote = () => {
    if (!selectedPriceItem || !itemForm.quantity) { alert('Seleziona voce e quantità'); return; }
    const qty = parseFloat(itemForm.quantity) || 0;
    const supply = qty * selectedPriceItem.price_supply;
    const work = qty * selectedPriceItem.price_work;
    const baseAmount = supply + work;
    let totalAdj = 0;
    const procAcc = itemForm.accessory_items.map(acc => {
      const accItem = priceItems.find(p => p.id === acc.price_item_id);
      if (accItem?.percentage_value) {
        const adj = baseAmount * (accItem.percentage_value / 100);
        totalAdj += adj;
        return { price_item_id: accItem.id, code: accItem.code, description: accItem.description, percentage_value: accItem.percentage_value, adjustment_amount: Math.round(adj * 100) / 100 };
      }
      return null;
    }).filter(Boolean);
    
    setQuoteForm(prev => ({ ...prev, items: [...prev.items, {
      price_item_id: selectedPriceItem.id, code: selectedPriceItem.code, description: selectedPriceItem.description,
      quantity: qty, unit: (itemForm.unit || selectedPriceItem.unit || '').trim(), price_supply: selectedPriceItem.price_supply, price_work: selectedPriceItem.price_work,
      total_supply: Math.round(supply * 100) / 100, total_work: Math.round(work * 100) / 100, base_total: Math.round(baseAmount * 100) / 100,
      accessory_items: procAcc, total_adjustment: Math.round(totalAdj * 100) / 100, total_amount: Math.round((baseAmount + totalAdj) * 100) / 100,
      section_id: itemForm.section_id || '',
    }] }));
    setShowAddItem(false);
    setItemForm({ price_item_id: '', quantity: '', unit: '', section_id: '', accessory_items: [] });
    setSearchItemTerm('');
    setShowItemResults(false);
  };
  
  const moveItem = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= quoteForm.items.length) return;
    setQuoteForm(prev => {
      const items = [...prev.items];
      [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
      return { ...prev, items };
    });
    setEditingItemIdx(prev => (prev === idx ? newIdx : prev === newIdx ? idx : prev));
  };

  const duplicateItem = (idx) => {
    setQuoteForm(prev => {
      const items = [...prev.items];
      const copy = { ...items[idx], accessory_items: (items[idx].accessory_items || []).map(a => ({ ...a })) };
      items.splice(idx + 1, 0, copy);
      return { ...prev, items };
    });
    setEditingItemIdx(null);
  };

  const handleCreateSection = () => {
    const title = sectionTitleInput.trim();
    if (!title) return;
    const color = SECTION_COLORS[quoteForm.sections.length % SECTION_COLORS.length];
    const section = { id: (crypto.randomUUID ? crypto.randomUUID() : `sec_${Date.now()}_${Math.random()}`), title, color: color.bg, text_color: color.text };
    setQuoteForm(prev => ({ ...prev, sections: [...prev.sections, section] }));
    setSectionTitleInput('');
    setShowCreateSectionDialog(false);
    return section;
  };

  const renameSection = (sectionId, title) => {
    setQuoteForm(prev => ({ ...prev, sections: prev.sections.map(s => s.id === sectionId ? { ...s, title } : s) }));
  };

  const deleteSection = (sectionId) => {
    setQuoteForm(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId),
      items: prev.items.map(it => it.section_id === sectionId ? { ...it, section_id: '' } : it),
    }));
  };

  const assignItemSection = (idx, sectionId) => {
    setQuoteForm(prev => ({ ...prev, items: prev.items.map((it, i) => i === idx ? { ...it, section_id: sectionId } : it) }));
  };

  const openChangeService = (idx) => { setChangeServiceIdx(idx); setChangeServiceSearch(''); setShowChangeService(true); };

  const handleChangeService = (newPriceItem) => {
    setQuoteForm(prev => ({
      ...prev,
      items: prev.items.map((it, i) => {
        if (i !== changeServiceIdx) return it;
        const qty = it.quantity || 0;
        const ts = Math.round(qty * newPriceItem.price_supply * 100) / 100;
        const tw = Math.round(qty * newPriceItem.price_work * 100) / 100;
        const base = ts + tw;
        const adjTotal = (it.accessory_items || []).reduce((s, a) => s + (base * ((a.percentage_value || 0) / 100)), 0);
        return {
          ...it,
          price_item_id: newPriceItem.id, code: newPriceItem.code, description: newPriceItem.description,
          price_supply: newPriceItem.price_supply, price_work: newPriceItem.price_work,
          total_supply: ts, total_work: tw, base_total: Math.round(base * 100) / 100,
          total_adjustment: Math.round(adjTotal * 100) / 100, total_amount: Math.round((base + adjTotal) * 100) / 100,
        };
      }),
    }));
    setShowChangeService(false);
    setChangeServiceIdx(null);
  };

  const itemsCountBySection = (() => {
    const counts = {};
    quoteForm.items.forEach(it => { if (it.section_id) counts[it.section_id] = (counts[it.section_id] || 0) + 1; });
    return counts;
  })();
  const sectionTotalsById = (() => {
    const totalsMap = {};
    quoteForm.items.forEach(it => { if (it.section_id) totalsMap[it.section_id] = (totalsMap[it.section_id] || 0) + (it.total_amount || 0); });
    return totalsMap;
  })();
  const sectionById = (id) => quoteForm.sections.find(s => s.id === id);

  const handleSendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);
    try {
      const res = await api.post('/quotes/ai/chat', { message: userMsg, thread_id: threadId });
      if (res.data.thread_id) setThreadId(res.data.thread_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) { 
      setChatMessages(prev => [...prev, { role: 'assistant', content: err.response?.data?.detail || 'Errore. Riprova.' }]); 
    }
    finally { setChatLoading(false); }
  };
  
  const totals = (() => {
    const totalSupply = quoteForm.items.reduce((s, i) => s + (i.total_supply || 0), 0);
    const totalWork = quoteForm.items.reduce((s, i) => s + (i.total_work || 0), 0);
    const totalAmount = quoteForm.items.reduce((s, i) => s + (i.total_amount || 0), 0);
    const discountAmount = totalAmount * (quoteForm.discount_percentage || 0) / 100;
    return { totalSupply, totalWork, totalAmount, discountAmount, finalAmount: totalAmount - discountAmount };
  })();
  
  const formatCurrency = (amt) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amt || 0);
  const filteredQuotes = quotes.filter(q => (!searchTerm || q.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) || q.client_name?.toLowerCase().includes(searchTerm.toLowerCase())) && (statusFilter === '__all__' || q.status === statusFilter));
  
  const getStatusBadge = (status) => {
    const map = { draft: { l: 'Bozza', c: 'bg-slate-100 text-slate-700' }, sent: { l: 'Inviato', c: 'bg-blue-100 text-blue-700' }, accepted: { l: 'Accettato', c: 'bg-green-100 text-green-700' }, rejected: { l: 'Rifiutato', c: 'bg-red-100 text-red-700' } };
    const s = map[status] || map.draft;
    return <Badge variant="outline" className={s.c}>{s.l}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;

  // VISTA LISTA
  if (activeView === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-slate-900">Preventivi</h1>
          {isRole('admin', 'qhse', 'capo_cantiere') && <Button onClick={handleNewQuote}><Plus size={16} className="mr-1" /> Nuovo Preventivo</Button>}
        </div>

        {draft && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="quote-draft-banner">
            <span>
              Hai un preventivo non salvato{draft.quoteForm.quote_number ? ` (${draft.quoteForm.quote_number}${draft.quoteForm.client_name ? ` - ${draft.quoteForm.client_name}` : ''})` : ''}, salvato in automatico alle {new Date(draft.savedAt).toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit' })}.
            </span>
            <span className="flex gap-2">
              <Button size="sm" onClick={restoreDraft} data-testid="quote-draft-restore">Riprendi</Button>
              <Button size="sm" variant="outline" onClick={() => { if (window.confirm('Eliminare la bozza non salvata?')) clearDraft(); }} data-testid="quote-draft-discard">Scarta</Button>
            </span>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
            <Input placeholder="Cerca..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti</SelectItem>
              <SelectItem value="draft">Bozza</SelectItem>
              <SelectItem value="sent">Inviato</SelectItem>
              <SelectItem value="accepted">Accettato</SelectItem>
              <SelectItem value="rejected">Rifiutato</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numero</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="hidden md:table-cell">Descrizione</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="w-36"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuotes.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessun preventivo</TableCell></TableRow>
                ) : filteredQuotes.map(q => (
                  <TableRow key={q.id}>
                    <TableCell><Badge variant="outline" className="bg-blue-50 text-blue-700 font-mono">{q.quote_number}</Badge></TableCell>
                    <TableCell className="font-medium">{q.client_name}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-600 max-w-[200px] truncate">{q.project_description}</TableCell>
                    <TableCell className="text-sm">{q.date}</TableCell>
                    <TableCell className="text-right font-bold text-green-700">{formatCurrency(q.final_amount)}</TableCell>
                    <TableCell>{getStatusBadge(q.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditQuote(q)} title="Modifica"><Edit size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDuplicateQuote(q)} title="Duplica"><Copy size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(q.id, 'pdf')} title="PDF"><Download size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(q.id, 'excel')} title="Excel"><FileSpreadsheet size={14} /></Button>
                        {isRole('admin') && <Button variant="ghost" size="sm" onClick={() => handleDeleteQuote(q.id)} className="text-red-600"><Trash2 size={14} /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // VISTA EDITOR (pagina intera con chat)
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => setActiveView('list')}><ArrowLeft size={16} className="mr-1" /> Indietro</Button>
        <h1 className="text-xl font-bold">{editingQuote ? 'Modifica' : 'Nuovo'} Preventivo</h1>
      </div>
      
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* Form - 7 colonne */}
        <div className="xl:col-span-7 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Dati Cliente</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Numero</Label><Input value={quoteForm.quote_number} onChange={(e) => setQuoteForm({...quoteForm, quote_number: e.target.value})} className="h-8" /></div>
                  <div><Label className="text-xs">Data</Label><Input type="date" value={quoteForm.date} onChange={(e) => setQuoteForm({...quoteForm, date: e.target.value})} className="h-8" /></div>
                </div>
                <div><Label className="text-xs">Cliente *</Label><Input value={quoteForm.client_name} onChange={(e) => setQuoteForm({...quoteForm, client_name: e.target.value})} className="h-8" /></div>
                <div><Label className="text-xs">Indirizzo</Label><Input value={quoteForm.client_address} onChange={(e) => setQuoteForm({...quoteForm, client_address: e.target.value})} className="h-8" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Email</Label><Input value={quoteForm.client_email} onChange={(e) => setQuoteForm({...quoteForm, client_email: e.target.value})} className="h-8" /></div>
                  <div><Label className="text-xs">Telefono</Label><Input value={quoteForm.client_phone} onChange={(e) => setQuoteForm({...quoteForm, client_phone: e.target.value})} className="h-8" /></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Progetto</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div><Label className="text-xs">Descrizione</Label><Textarea value={quoteForm.project_description} onChange={(e) => setQuoteForm({...quoteForm, project_description: e.target.value})} rows={2} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Località</Label><Input value={quoteForm.location} onChange={(e) => setQuoteForm({...quoteForm, location: e.target.value})} className="h-8" /></div>
                  <div><Label className="text-xs">Validità (gg)</Label><Input type="number" value={quoteForm.validity_days} onChange={(e) => setQuoteForm({...quoteForm, validity_days: parseInt(e.target.value)||30})} className="h-8" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Stato</Label>
                    <Select value={quoteForm.status} onValueChange={(v) => setQuoteForm({...quoteForm, status: v})}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="draft">Bozza</SelectItem><SelectItem value="sent">Inviato</SelectItem><SelectItem value="accepted">Accettato</SelectItem><SelectItem value="rejected">Rifiutato</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Sconto %</Label><Input type="number" value={quoteForm.discount_percentage} onChange={(e) => setQuoteForm({...quoteForm, discount_percentage: parseFloat(e.target.value)||0})} className="h-8" min="0" max="100" /></div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Riferimenti e Condizioni</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Ns. Rif.</Label><Input value={quoteForm.ns_rif} onChange={(e) => setQuoteForm({...quoteForm, ns_rif: e.target.value})} className="h-8" placeholder="Nostro riferimento" data-testid="quote-ns-rif" /></div>
                <div><Label className="text-xs">Vs. Rif.</Label><Input value={quoteForm.vs_rif} onChange={(e) => setQuoteForm({...quoteForm, vs_rif: e.target.value})} className="h-8" placeholder="Vostro riferimento" data-testid="quote-vs-rif" /></div>
                <div><Label className="text-xs">Work Order</Label><Input value={quoteForm.work_order} onChange={(e) => setQuoteForm({...quoteForm, work_order: e.target.value})} className="h-8" placeholder="N. ordine" data-testid="quote-work-order" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Inizio Lavoro</Label><Input value={quoteForm.inizio_lavoro} onChange={(e) => setQuoteForm({...quoteForm, inizio_lavoro: e.target.value})} className="h-8" placeholder="Come da contratto" data-testid="quote-inizio-lavoro" /></div>
                <div><Label className="text-xs">Durata Lavoro</Label><Input value={quoteForm.durata_lavoro} onChange={(e) => setQuoteForm({...quoteForm, durata_lavoro: e.target.value})} className="h-8" placeholder="Come da contratto" data-testid="quote-durata-lavoro" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Oneri a Nostro carico</Label><Input value={quoteForm.oneri_nostri} onChange={(e) => setQuoteForm({...quoteForm, oneri_nostri: e.target.value})} className="h-8" placeholder="Come da contratto" data-testid="quote-oneri-nostri" /></div>
                <div><Label className="text-xs">Oneri a Vostro carico</Label><Input value={quoteForm.oneri_vostri} onChange={(e) => setQuoteForm({...quoteForm, oneri_vostri: e.target.value})} className="h-8" placeholder="Come da contratto" data-testid="quote-oneri-vostri" /></div>
              </div>
              <div><Label className="text-xs">Attrezzatura</Label><Input value={quoteForm.attrezzatura} onChange={(e) => setQuoteForm({...quoteForm, attrezzatura: e.target.value})} className="h-8" placeholder="Come da contratto" data-testid="quote-attrezzatura" /></div>
              <div><Label className="text-xs">Condizioni di Pagamento</Label><Textarea value={quoteForm.terms || ''} onChange={(e) => setQuoteForm({...quoteForm, terms: e.target.value})} rows={2} placeholder="Es: 50% alla conferma, 50% a fine lavori" data-testid="quote-terms" /></div>
              <div><Label className="text-xs">Coordinate Bancarie (C.C.)</Label><Input value={quoteForm.bank_details || ''} onChange={(e) => setQuoteForm({...quoteForm, bank_details: e.target.value})} className="h-8" placeholder="IBAN / Banca" data-testid="quote-bank" /></div>
              <div><Label className="text-xs">Note Aggiuntive</Label><Textarea value={quoteForm.notes || ''} onChange={(e) => setQuoteForm({...quoteForm, notes: e.target.value})} rows={2} placeholder="Note opzionali..." data-testid="quote-notes" /></div>
            </CardContent>
          </Card>

          <Card className="border-orange-200">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-orange-700">Nota Finale / Servizio Aggiuntivo (opzionale)</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-slate-400">Il servizio aggiuntivo è fuori prezziario e NON modifica il totale ufficiale del preventivo: nel PDF compare come nota a parte, con un totale opzionale a cui il cliente può aderire in fase di accettazione.</p>
              <div><Label className="text-xs">Nota finale</Label><Textarea value={quoteForm.extra_note || ''} onChange={(e) => setQuoteForm({...quoteForm, extra_note: e.target.value})} rows={2} placeholder="Es: eventuali lavorazioni extra da concordare a parte..." data-testid="quote-extra-note" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2"><Label className="text-xs">Servizio aggiuntivo (descrizione)</Label><Input value={quoteForm.extra_service_description || ''} onChange={(e) => setQuoteForm({...quoteForm, extra_service_description: e.target.value})} className="h-8" placeholder="Es: verniciatura ringhiere esterne" data-testid="quote-extra-service-desc" /></div>
                <div><Label className="text-xs">Importo (EUR)</Label><Input type="number" step="0.01" min="0" value={quoteForm.extra_service_amount || ''} onChange={(e) => setQuoteForm({...quoteForm, extra_service_amount: parseFloat(e.target.value) || 0})} className="h-8" placeholder="0.00" data-testid="quote-extra-service-amount" /></div>
              </div>
              {quoteForm.extra_service_amount > 0 && (
                <p className="text-xs text-orange-700 font-medium">Totale opzionale in caso di accettazione: {formatCurrency(totals.finalAmount + quoteForm.extra_service_amount)} <span className="text-slate-400 font-normal">(il totale ufficiale resta {formatCurrency(totals.finalAmount)})</span></p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm">Voci ({quoteForm.items.length})</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowCreateSectionDialog(true)} data-testid="add-section-title"><Layers size={14} className="mr-1" /> Nuova sezione</Button>
                <Button size="sm" onClick={() => setShowAddItem(true)}><Plus size={14} className="mr-1" /> Aggiungi</Button>
              </div>
            </CardHeader>
            <CardContent>
              {quoteForm.sections.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b">
                  <span className="text-xs text-slate-400 mr-1">Sezioni:</span>
                  {quoteForm.sections.map((sec) => (
                    <div key={sec.id} className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full border text-xs" style={{ backgroundColor: sec.color, borderColor: `${sec.text_color}33` }}>
                      <Input
                        value={sec.title}
                        onChange={(e) => renameSection(sec.id, e.target.value)}
                        className="h-5 text-xs font-semibold border-none bg-transparent shadow-none p-0 w-24 focus-visible:ring-0"
                        style={{ color: sec.text_color }}
                        data-testid={`section-title-${sec.id}`}
                      />
                      <span style={{ color: sec.text_color }} className="opacity-70">({itemsCountBySection[sec.id] || 0})</span>
                      <button onClick={() => deleteSection(sec.id)} className="text-red-500 hover:text-red-700 font-bold leading-none ml-0.5" title="Elimina sezione (le voci restano, perdono solo il colore)" data-testid={`delete-section-${sec.id}`}>&times;</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="max-h-[60vh] overflow-y-auto">
                {quoteForm.items.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 text-sm">Nessuna voce</div>
                ) : (
                  <div className="space-y-2 pr-1">
                    {quoteForm.items.map((item, idx) => {
                      const sec = item.section_id ? sectionById(item.section_id) : null;
                      const nextItem = quoteForm.items[idx + 1];
                      const isSectionEnd = sec && (!nextItem || nextItem.section_id !== item.section_id);
                      return (
                      <div key={idx}>
                      <div className="p-2 border rounded-lg text-sm cursor-pointer hover:border-blue-200" style={sec ? { backgroundColor: sec.color, borderColor: `${sec.text_color}44` } : undefined} onClick={() => editingItemIdx !== idx && setEditingItemIdx(idx)}>
                        {sec && <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: sec.text_color }}>{sec.title}</p>}
                        {editingItemIdx === idx ? (
                          <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between flex-wrap gap-1">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <Badge variant="outline" className="font-mono text-xs flex-shrink-0">{item.code}</Badge>
                                <span className="text-xs text-slate-400 truncate">{item.description}</span>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button size="sm" variant="outline" onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="h-6 w-6 p-0" title="Sposta su"><ArrowUp size={13} /></Button>
                                <Button size="sm" variant="outline" onClick={() => moveItem(idx, 1)} disabled={idx === quoteForm.items.length - 1} className="h-6 w-6 p-0" title="Sposta giù"><ArrowDown size={13} /></Button>
                                <Button size="sm" variant="outline" onClick={() => openChangeService(idx)} className="h-6 text-xs" title="Cambia la voce di prezziario mantenendo quantità e u.m." data-testid={`change-service-${idx}`}><Repeat size={13} className="mr-1" /> Cambia voce</Button>
                                <Button size="sm" variant="outline" onClick={() => duplicateItem(idx)} className="h-6 w-6 p-0" title="Duplica voce" data-testid={`duplicate-item-${idx}`}><Copy size={13} /></Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingItemIdx(null)} className="h-6 text-xs" data-testid={`close-edit-${idx}`}>Chiudi</Button>
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Sezione</Label>
                              <Select value={item.section_id || '__none__'} onValueChange={(v) => assignItemSection(idx, v === '__none__' ? '' : v)}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`assign-section-${idx}`}><SelectValue placeholder="Nessuna sezione" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">Nessuna sezione</SelectItem>
                                  {quoteForm.sections.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              <div><Label className="text-xs">Quantità</Label><Input type="number" step="0.01" value={item.quantity} onChange={(e) => {
                                const qty = parseFloat(e.target.value) || 0;
                                const ts = Math.round(qty * item.price_supply * 100) / 100;
                                const tw = Math.round(qty * item.price_work * 100) / 100;
                                const base = ts + tw;
                                const adjTotal = (item.accessory_items || []).reduce((s, a) => s + (base * ((a.percentage_value || 0) / 100)), 0);
                                setQuoteForm(prev => ({...prev, items: prev.items.map((it, i) => i === idx ? {...it, quantity: qty, total_supply: ts, total_work: tw, base_total: Math.round(base * 100) / 100, total_adjustment: Math.round(adjTotal * 100) / 100, total_amount: Math.round((base + adjTotal) * 100) / 100} : it)}));
                              }} className="h-7 text-xs" /></div>
                              <div><Label className="text-xs">U.M.</Label><Input value={item.unit || ''} placeholder="u.m." onChange={(e) => {
                                const u = e.target.value;
                                setQuoteForm(prev => ({...prev, items: prev.items.map((it, i) => i === idx ? {...it, unit: u} : it)}));
                              }} className="h-7 text-xs" data-testid={`edit-unit-${idx}`} /></div>
                              <div><Label className="text-xs">P. Fornitura</Label><Input type="number" step="0.01" value={item.price_supply} onChange={(e) => {
                                const ps = parseFloat(e.target.value) || 0;
                                const ts = Math.round(item.quantity * ps * 100) / 100;
                                const tw = item.total_work;
                                const base = ts + tw;
                                const adjTotal = (item.accessory_items || []).reduce((s, a) => s + (base * ((a.percentage_value || 0) / 100)), 0);
                                setQuoteForm(prev => ({...prev, items: prev.items.map((it, i) => i === idx ? {...it, price_supply: ps, total_supply: ts, base_total: Math.round(base * 100) / 100, total_adjustment: Math.round(adjTotal * 100) / 100, total_amount: Math.round((base + adjTotal) * 100) / 100} : it)}));
                              }} className="h-7 text-xs" /></div>
                              <div><Label className="text-xs">P. Lavorazione</Label><Input type="number" step="0.01" value={item.price_work} onChange={(e) => {
                                const pw = parseFloat(e.target.value) || 0;
                                const tw = Math.round(item.quantity * pw * 100) / 100;
                                const ts = item.total_supply;
                                const base = ts + tw;
                                const adjTotal = (item.accessory_items || []).reduce((s, a) => s + (base * ((a.percentage_value || 0) / 100)), 0);
                                setQuoteForm(prev => ({...prev, items: prev.items.map((it, i) => i === idx ? {...it, price_work: pw, total_work: tw, base_total: Math.round(base * 100) / 100, total_adjustment: Math.round(adjTotal * 100) / 100, total_amount: Math.round((base + adjTotal) * 100) / 100} : it)}));
                              }} className="h-7 text-xs" /></div>
                            </div>
                            <div className="flex flex-wrap justify-between items-center gap-2">
                              <span className="font-bold text-green-700">{formatCurrency(item.total_amount)}</span>
                              <Button size="sm" variant="destructive" onClick={() => { setQuoteForm(prev => ({...prev, items: prev.items.filter((_, i) => i !== idx)})); setEditingItemIdx(null); }} className="h-7 text-xs flex-shrink-0" data-testid={`delete-item-${idx}`}>
                                <Trash2 size={13} className="mr-1" /> Elimina voce
                              </Button>
                            </div>
                            {item.accessory_items?.length > 0 && (
                              <div className="mt-1">
                                <Label className="text-xs text-amber-700">Voci accessorie %:</Label>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {item.accessory_items.map((a, ai) => (
                                    <span key={ai} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-xs">
                                      {a.code} - {a.description} ({a.percentage_value > 0 ? '+' : ''}{a.percentage_value}%)
                                      <button onClick={() => { setQuoteForm(prev => ({...prev, items: prev.items.map((it, i) => {
                                        if (i !== idx) return it;
                                        const newAcc = it.accessory_items.filter((_, j) => j !== ai);
                                        const base = (it.total_supply || 0) + (it.total_work || 0);
                                        const totalAdj = newAcc.reduce((s, ac) => s + (base * ((ac.percentage_value || 0) / 100)), 0);
                                        return {...it, accessory_items: newAcc, total_adjustment: Math.round(totalAdj * 100) / 100, total_amount: Math.round((base + totalAdj) * 100) / 100};
                                      })})); }} className="text-red-500 hover:text-red-700 font-bold" title="Rimuovi">&times;</button>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="mt-1"><Label className="text-xs">Nota voce</Label><Input className="h-7 text-xs" value={item.notes || ''} placeholder="Nota opzionale..." onChange={(e) => setQuoteForm(prev => ({...prev, items: prev.items.map((it, i) => i === idx ? {...it, notes: e.target.value} : it)}))} /></div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="font-mono text-xs">{item.code}</Badge>
                                <span className="text-xs text-slate-400">{item.quantity} {item.unit}</span>
                              </div>
                              <p className="text-xs text-slate-600 break-words">{item.description}</p>
                              {item.accessory_items?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {item.accessory_items.map((a, ai) => (
                                    <span key={ai} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[10px]">
                                      {a.code} - {a.description} ({a.percentage_value > 0 ? '+' : ''}{a.percentage_value}%)
                                      <button onClick={(e) => { e.stopPropagation(); setQuoteForm(prev => ({...prev, items: prev.items.map((it, i) => {
                                        if (i !== idx) return it;
                                        const newAcc = it.accessory_items.filter((_, j) => j !== ai);
                                        const base = (it.total_supply || 0) + (it.total_work || 0);
                                        const totalAdj = newAcc.reduce((s, ac) => s + (base * ((ac.percentage_value || 0) / 100)), 0);
                                        return {...it, accessory_items: newAcc, total_adjustment: Math.round(totalAdj * 100) / 100, total_amount: Math.round((base + totalAdj) * 100) / 100};
                                      })})); }} className="ml-0.5 text-red-500 hover:text-red-700 font-bold leading-none" title="Rimuovi voce accessoria">&times;</button>
                                    </span>
                                  ))}
                                </div>
                              )}
                              {item.notes && <p className="text-xs text-slate-400 italic">{item.notes}</p>}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <div className="text-right mr-1">
                                <p className="font-bold text-green-700 leading-tight">{formatCurrency(item.total_amount)}</p>
                                {item.total_supply > 0 && item.total_work > 0 && (
                                  <p className="text-[9px] text-slate-400 leading-tight" title="Fornitura e applicazione">
                                    Forn. {formatCurrency(item.total_supply)} + Appl. {formatCurrency(item.total_work)}
                                  </p>
                                )}
                              </div>
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); moveItem(idx, -1); }} disabled={idx === 0} className="h-6 w-6 p-0" title="Sposta su"><ArrowUp size={13} /></Button>
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); moveItem(idx, 1); }} disabled={idx === quoteForm.items.length - 1} className="h-6 w-6 p-0" title="Sposta giù"><ArrowDown size={13} /></Button>
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); duplicateItem(idx); }} className="h-6 w-6 p-0" title="Duplica voce" data-testid={`duplicate-item-${idx}`}><Copy size={13} /></Button>
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); if(confirm('Eliminare questa voce?')) setQuoteForm(prev => ({...prev, items: prev.items.filter((_, i) => i !== idx)})); }} className="text-red-500 border-red-200 hover:bg-red-50 h-6 px-1.5" data-testid={`remove-item-${idx}`}><Trash2 size={14} /></Button>
                            </div>
                          </div>
                        )}
                      </div>
                      {isSectionEnd && (
                        <div className="flex justify-between items-center px-2.5 py-1.5 mt-1 rounded-md text-xs font-bold" style={{ backgroundColor: sec.color, color: sec.text_color, borderLeft: `3px solid ${sec.text_color}` }} data-testid={`section-subtotal-${item.section_id}`}>
                          <span>Totale {sec.title}</span>
                          <span>{formatCurrency(sectionTotalsById[item.section_id] || 0)}</span>
                        </div>
                      )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {quoteForm.items.length > 0 && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <Label className="text-xs whitespace-nowrap">Applica % a tutte le voci:</Label>
                    <Select value="" onValueChange={(code) => {
                      const accItem = priceItems.find(p => p.id === code || p.code === code);
                      if (!accItem) return;
                      setQuoteForm(prev => ({
                        ...prev,
                        items: prev.items.map(item => {
                          if (item.is_header) return item;
                          const alreadyHas = (item.accessory_items || []).some(a => a.code === accItem.code);
                          if (alreadyHas) return item;
                          const base = (item.total_supply || 0) + (item.total_work || 0);
                          const adj = Math.round(base * (accItem.percentage_value || 0) / 100 * 100) / 100;
                          const newAcc = [...(item.accessory_items || []), { code: accItem.code, description: accItem.description, price_item_id: accItem.id, percentage_value: accItem.percentage_value, adjustment_amount: adj }];
                          const totalAdj = newAcc.reduce((s, a) => s + (a.adjustment_amount || 0), 0);
                          return { ...item, accessory_items: newAcc, total_adjustment: Math.round(totalAdj * 100) / 100, total_amount: Math.round((base + totalAdj) * 100) / 100 };
                        })
                      }));
                    }}>
                      <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Seleziona voce accessoria..." /></SelectTrigger>
                      <SelectContent>
                        {priceItems.filter(p => p.price_type !== 'standard').map(p => (
                          <SelectItem key={p.id} value={p.code}>{p.code} - {p.description} ({p.percentage_value > 0 ? '+' : ''}{p.percentage_value}%)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between"><span>Fornitura:</span><span className="text-blue-700">{formatCurrency(totals.totalSupply)}</span></div>
                    <div className="flex justify-between"><span>Lavoro:</span><span className="text-orange-700">{formatCurrency(totals.totalWork)}</span></div>
                    <div className="flex justify-between"><span>Subtotale:</span><span>{formatCurrency(totals.totalAmount)}</span></div>
                    {quoteForm.discount_percentage > 0 && <div className="flex justify-between"><span>Sconto {quoteForm.discount_percentage}%:</span><span className="text-red-500">- {formatCurrency(totals.discountAmount)}</span></div>}
                    <div className="flex justify-between font-bold text-lg pt-1 border-t"><span>TOTALE:</span><span className="text-green-700">{formatCurrency(totals.finalAmount)}</span></div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          <div className="sticky bottom-0 z-10 bg-white/95 backdrop-blur-sm pt-3 pb-1 border-t flex gap-3" data-testid="quote-action-buttons">
            <Button variant="outline" onClick={() => setActiveView('list')} className="flex-1">Annulla</Button>
            <Button onClick={handleSaveQuote} className="flex-1 bg-green-600 hover:bg-green-700">{editingQuote ? 'Aggiorna' : 'Salva'}</Button>
          </div>
        </div>
        
                {/* Chat AI - 5 colonne */}
        <div className="xl:col-span-5">
          <div className="xl:sticky xl:top-20">
          <Card className="flex flex-col" style={{maxHeight: 'calc(100vh - 6rem)'}}>
            <CardHeader className="pb-2 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-t-lg flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Bot className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold">Rocchino Coverino</CardTitle>
                    <p className="text-xs text-blue-200">Assistente Tecnico Prezziario</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowPdfModal(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white gap-1">
                  <FileText size={14} /> PDF Prezziario
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
              <ScrollArea className="flex-1 p-4" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
                <div className="space-y-4">
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={'flex gap-3 ' + (msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                      {msg.role === 'assistant' && <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0"><Bot className="w-5 h-5 text-blue-700" /></div>}
                      <div className={'max-w-[80%] p-3 rounded-xl ' + (msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-slate-100 rounded-bl-sm')}>
                        <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                      </div>
                      {msg.role === 'user' && <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0"><User className="w-5 h-5 text-slate-600" /></div>}
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center"><Bot className="w-5 h-5 text-blue-700" /></div>
                      <div className="flex gap-1 items-center p-3 bg-slate-100 rounded-xl">
                        <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></span>
                        <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}}></span>
                        <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}}></span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              </ScrollArea>
              <div className="p-4 border-t bg-slate-50">
                <div className="flex gap-2">
                  <Input placeholder="Chiedi a Rocchino..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendChat()} disabled={chatLoading} className="flex-1" />
                  <Button onClick={handleSendChat} disabled={chatLoading || !chatInput.trim()} className="bg-blue-700 hover:bg-blue-800"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
      
                  {/* Dialog Aggiungi Voce */}
      <Dialog open={showAddItem} onOpenChange={(open) => { setShowAddItem(open); if (!open) { setSearchItemTerm(''); setShowItemResults(false); } }}>
        <DialogContent className="max-w-5xl w-[95vw] h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg">Aggiungi Voce al Preventivo</DialogTitle>
              <Button variant="outline" size="sm" onClick={() => setShowPdfModal(true)} className="gap-1">
                <FileText size={14} /> Consulta PDF
              </Button>
            </div>
          </DialogHeader>
          
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
            {/* LEFT: Selezione voce */}
            <div className="flex-1 flex flex-col border-r overflow-hidden">
              {/* Search bar */}
              <div className="p-4 border-b bg-slate-50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                  <Input 
                    placeholder="Cerca per codice, descrizione o sottocategoria..." 
                    value={searchItemTerm}
                    onChange={(e) => { setSearchItemTerm(e.target.value); if (!e.target.value) { setItemForm({...itemForm, price_item_id: '', accessory_items: []}); } }}
                    className="pl-10 h-10 text-base"
                    data-testid="search-price-item"
                    autoFocus
                  />
                </div>
                {selectedPriceItem && (
                  <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-300 flex justify-between items-center">
                    <span className="text-sm font-medium"><span className="font-mono text-blue-700 font-bold">{selectedPriceItem.code}</span> — {selectedPriceItem.description}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-green-700">{formatCurrency(selectedPriceItem.price_total)}/{selectedPriceItem.unit}</span>
                      <button onClick={() => { setItemForm({...itemForm, price_item_id: '', accessory_items: []}); setSearchItemTerm(''); }} className="text-red-400 hover:text-red-600 font-bold text-lg leading-none">&times;</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Price list table */}
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Codice</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Descrizione</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600 hidden md:table-cell">Sottocategoria</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Forn.</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Lav.</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Totale</th>
                      <th className="text-center px-4 py-2 font-semibold text-slate-600">U.M.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceItems.filter(p => (!p.price_type || p.price_type === 'standard') && (!searchItemTerm || p.code.toLowerCase().includes(searchItemTerm.toLowerCase()) || p.description.toLowerCase().includes(searchItemTerm.toLowerCase()) || (p.subcategory && p.subcategory.toLowerCase().includes(searchItemTerm.toLowerCase())))).length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-8 text-slate-400">Nessuna voce trovata</td></tr>
                    ) : priceItems.filter(p => (!p.price_type || p.price_type === 'standard') && (!searchItemTerm || p.code.toLowerCase().includes(searchItemTerm.toLowerCase()) || p.description.toLowerCase().includes(searchItemTerm.toLowerCase()) || (p.subcategory && p.subcategory.toLowerCase().includes(searchItemTerm.toLowerCase())))).map(p => (
                      <tr key={p.id} 
                        onClick={() => { setItemForm({...itemForm, price_item_id: p.id, unit: p.unit || '', accessory_items: []}); setSearchItemTerm(''); }}
                        className={`cursor-pointer border-b transition-colors ${itemForm.price_item_id === p.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-slate-50'}`}>
                        <td className="px-4 py-2.5"><span className="font-mono text-blue-600 font-semibold text-xs">{p.code}</span></td>
                        <td className="px-4 py-2.5">{p.description}</td>
                        <td className="px-4 py-2.5 text-slate-500 hidden md:table-cell">{p.subcategory || '-'}</td>
                        <td className="px-4 py-2.5 text-right text-blue-700">{formatCurrency(p.price_supply)}</td>
                        <td className="px-4 py-2.5 text-right text-orange-700">{formatCurrency(p.price_work)}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-green-700">{formatCurrency(p.price_total)}</td>
                        <td className="px-4 py-2.5 text-center text-slate-500">{p.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* RIGHT: Dettagli e riepilogo */}
            <div className="w-full md:w-[340px] flex flex-col overflow-y-auto bg-slate-50">
              <div className="p-4 space-y-4">
                {/* Quantità e U.M. modificabile */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <Label className="text-sm font-semibold">Quantità *</Label>
                    <Input type="number" step="0.01" value={itemForm.quantity} onChange={(e) => setItemForm({...itemForm, quantity: e.target.value})} className="mt-1 h-10 text-base" placeholder="0.00" data-testid="item-quantity" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">U.M.</Label>
                    <Input
                      value={itemForm.unit || ''}
                      onChange={(e) => setItemForm({...itemForm, unit: e.target.value})}
                      className="mt-1 h-10 text-base"
                      placeholder={selectedPriceItem?.unit || 'u.m.'}
                      title="Modifica liberamente l'unità di misura (es. giorno, ore, mq...)"
                      data-testid="item-unit"
                    />
                  </div>
                </div>

                {quoteForm.sections.length > 0 && (
                  <div>
                    <Label className="text-sm font-semibold">Sezione</Label>
                    <Select value={itemForm.section_id || '__none__'} onValueChange={(v) => setItemForm({ ...itemForm, section_id: v === '__none__' ? '' : v })}>
                      <SelectTrigger className="mt-1 h-10 text-base" data-testid="item-section"><SelectValue placeholder="Nessuna sezione" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Nessuna sezione</SelectItem>
                        {quoteForm.sections.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Chat Rocchino - conversazione completa */}
                {chatMessages.length > 1 && (
                  <div className="flex flex-col border rounded-lg overflow-hidden bg-white">
                    <div className="px-3 py-2 bg-gradient-to-r from-blue-900 to-blue-700 flex items-center gap-2">
                      <Bot className="w-4 h-4 text-white" />
                      <span className="text-white text-xs font-semibold">Chat Rocchino Coverino</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{height: '220px'}}>
                      {chatMessages.map((msg, i) => (
                        <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          {msg.role === 'assistant' && <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1"><Bot className="w-3 h-3 text-blue-700" /></div>}
                          <div className={`max-w-[85%] px-2.5 py-1.5 rounded-lg text-xs leading-relaxed ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-700 rounded-bl-sm'}`}>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Voci accessorie */}
                {selectedPriceItem && itemForm.quantity && (
                  <>
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <Label className="text-amber-800 font-semibold text-xs">Aumento/Sconto</Label>
                      <div className="mt-1 space-y-1 max-h-[120px] overflow-y-auto">
                        {priceItems.filter(p => p.price_type === 'percentage_increase' || p.price_type === 'percentage_discount').length === 0 ? (
                          <p className="text-xs text-amber-600 italic">Nessuna voce</p>
                        ) : priceItems.filter(p => p.price_type === 'percentage_increase' || p.price_type === 'percentage_discount').map(acc => (
                          <label key={acc.id} className="flex items-center gap-2 p-1.5 bg-white rounded border cursor-pointer hover:bg-amber-100 text-xs">
                            <input type="checkbox" checked={itemForm.accessory_items.some(a => a.price_item_id === acc.id)} onChange={(e) => {
                              if (e.target.checked) setItemForm({...itemForm, accessory_items: [...itemForm.accessory_items, {price_item_id: acc.id}]});
                              else setItemForm({...itemForm, accessory_items: itemForm.accessory_items.filter(a => a.price_item_id !== acc.id)});
                            }} className="w-3.5 h-3.5" />
                            <span className="flex-1">{acc.code} - {acc.description}</span>
                            <span className={'font-bold ' + (acc.percentage_value > 0 ? 'text-orange-600' : 'text-green-600')}>{acc.percentage_value > 0 ? '+' : ''}{acc.percentage_value}%</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Riepilogo prezzi */}
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-slate-500">Fornitura</span><span className="text-blue-700 font-medium">{formatCurrency((parseFloat(itemForm.quantity)||0) * selectedPriceItem.price_supply)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Lavorazione</span><span className="text-orange-700 font-medium">{formatCurrency((parseFloat(itemForm.quantity)||0) * selectedPriceItem.price_work)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Subtotale</span><span className="font-medium">{formatCurrency(baseTotal)}</span></div>
                      </div>
                      {itemForm.accessory_items.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-green-200 space-y-1">
                          {itemForm.accessory_items.map(acc => {
                            const accItem = priceItems.find(p => p.id === acc.price_item_id);
                            const adj = baseTotal * ((accItem?.percentage_value || 0) / 100);
                            return <div key={acc.price_item_id} className="flex justify-between text-xs"><span>{accItem?.code}</span><span className={adj > 0 ? 'text-orange-600' : 'text-green-600'}>{adj > 0 ? '+' : ''}{formatCurrency(adj)}</span></div>;
                          })}
                        </div>
                      )}
                      <div className="mt-2 pt-2 border-t-2 border-green-300 flex justify-between items-center">
                        <p className="font-bold text-sm">TOTALE</p>
                        <p className="font-bold text-green-700 text-xl">{formatCurrency(previewTotal)}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Action buttons - sticky bottom */}
              <div className="mt-auto p-4 border-t bg-white flex gap-3">
                <Button variant="outline" onClick={() => setShowAddItem(false)} className="flex-1">Annulla</Button>
                <Button onClick={handleAddItemToQuote} disabled={!itemForm.price_item_id || !itemForm.quantity} className="flex-1">Aggiungi</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Modal PDF Prezziario - Schermo Intero */}
      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-4 pb-3 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg">PDF Prezziario Contratto</DialogTitle>
              <div className="flex items-center gap-2">
                <p className="text-xs text-slate-500">Usa Ctrl+F per cercare nel PDF</p>
                <Button variant="outline" size="sm" onClick={() => window.open('/prezziario_contratto.pdf', '_blank')} className="gap-1">
                  <Download size={14} /> Apri in nuova scheda
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <iframe 
              src="/prezziario_contratto.pdf" 
              className="w-full h-full border-0"
              title="Prezziario Contratto PDF"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Nuova Sezione */}
      <Dialog open={showCreateSectionDialog} onOpenChange={(open) => { setShowCreateSectionDialog(open); if (!open) setSectionTitleInput(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuova sezione</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Titolo (es. Tetto galleggiante, Mantello, Fondo)</Label>
            <Input
              value={sectionTitleInput}
              onChange={(e) => setSectionTitleInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateSection()}
              placeholder="Titolo sezione..."
              autoFocus
              data-testid="header-title-input"
            />
            <p className="text-xs text-slate-400">Le viene assegnato un colore automaticamente. Poi assegna le voci a questa sezione: prenderanno lo stesso colore e mostreranno il titolo della sezione sopra di loro.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSectionDialog(false)}>Annulla</Button>
            <Button onClick={handleCreateSection} disabled={!sectionTitleInput.trim()} data-testid="confirm-add-header">Crea sezione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Cambia Voce (sostituisce codice/descrizione/prezzi mantenendo quantità e u.m.) */}
      <Dialog open={showChangeService} onOpenChange={(open) => { setShowChangeService(open); if (!open) { setChangeServiceSearch(''); setChangeServiceIdx(null); } }}>
        <DialogContent className="max-w-3xl w-[92vw] h-[75vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <DialogTitle className="text-lg">Cambia voce</DialogTitle>
            <p className="text-xs text-slate-500">Sostituisce la voce di prezziario mantenendo quantità ({changeServiceIdx != null ? quoteForm.items[changeServiceIdx]?.quantity : ''} {changeServiceIdx != null ? quoteForm.items[changeServiceIdx]?.unit : ''}) e note già inserite.</p>
          </DialogHeader>
          <div className="p-4 border-b bg-slate-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input placeholder="Cerca per codice, descrizione o sottocategoria..." value={changeServiceSearch} onChange={(e) => setChangeServiceSearch(e.target.value)} className="pl-10 h-10" autoFocus data-testid="change-service-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">Codice</th>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">Descrizione</th>
                  <th className="text-right px-4 py-2 font-semibold text-slate-600">Forn.</th>
                  <th className="text-right px-4 py-2 font-semibold text-slate-600">Lav.</th>
                  <th className="text-center px-4 py-2 font-semibold text-slate-600">U.M.</th>
                </tr>
              </thead>
              <tbody>
                {priceItems.filter(p => (!p.price_type || p.price_type === 'standard') && (!changeServiceSearch || p.code.toLowerCase().includes(changeServiceSearch.toLowerCase()) || p.description.toLowerCase().includes(changeServiceSearch.toLowerCase()) || (p.subcategory && p.subcategory.toLowerCase().includes(changeServiceSearch.toLowerCase())))).length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-400">Nessuna voce trovata</td></tr>
                ) : priceItems.filter(p => (!p.price_type || p.price_type === 'standard') && (!changeServiceSearch || p.code.toLowerCase().includes(changeServiceSearch.toLowerCase()) || p.description.toLowerCase().includes(changeServiceSearch.toLowerCase()) || (p.subcategory && p.subcategory.toLowerCase().includes(changeServiceSearch.toLowerCase())))).map(p => (
                  <tr key={p.id} onClick={() => handleChangeService(p)} className="cursor-pointer border-b hover:bg-blue-50 transition-colors" data-testid={`change-service-option-${p.code}`}>
                    <td className="px-4 py-2.5"><span className="font-mono text-blue-600 font-semibold text-xs">{p.code}</span></td>
                    <td className="px-4 py-2.5">{p.description}</td>
                    <td className="px-4 py-2.5 text-right text-blue-700">{formatCurrency(p.price_supply)}</td>
                    <td className="px-4 py-2.5 text-right text-orange-700">{formatCurrency(p.price_work)}</td>
                    <td className="px-4 py-2.5 text-center text-slate-500">{p.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t bg-white">
            <Button variant="outline" onClick={() => setShowChangeService(false)} className="w-full">Annulla</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
